import React, { Component } from 'react';
import { Form, Header, Icon, Message, Divider } from 'semantic-ui-react';
import { getSnapshot } from 'mobx-state-tree';
import { observer } from 'mobx-react';

/**
 * Per-user Report Sharing preferences. Controls how Murphy meeting reports
 * are distributed to the user themselves and to meeting participants.
 *
 * Mirrors the User.jsx Save pattern: edit a local snapshot, call
 * editUser.updateFromSnapshot() on save.
 */
class ReportSharingPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editUserSnapshot: getSnapshot(props.editUser),
            error: '',
            saved: false
        };
    }

    handleToggle = (field) => (_, data) => {
        const { editUserSnapshot } = this.state;
        this.setState({
            editUserSnapshot: { ...editUserSnapshot, [field]: !!data.checked },
            saved: false
        });
    }

    save = async () => {
        const { editUserSnapshot } = this.state;
        const { editUser } = this.props;
        this.setState({ error: '', saved: false });
        try {
            await editUser.updateFromSnapshot(editUserSnapshot);
            this.setState({ saved: true });
        } catch (error) {
            this.setState({ error: error.message });
        }
    }

    render() {
        const { editUserSnapshot, error, saved } = this.state;
        const { editUser } = this.props;

        return (
            <Form loading={editUser.isLoading} style={{ marginTop: '24px' }}>
                <Header as='h2' style={{ marginBottom: '4px' }}>
                    <Icon name='share alternate' />
                    <Header.Content>
                        Report Sharing
                        <Header.Subheader>Manage how your meeting reports are shared</Header.Subheader>
                    </Header.Content>
                </Header>

                {/* ---- Your own notifications ---- */}

                <div style={{ marginTop: '20px' }}>
                    <Form.Checkbox
                        toggle
                        label='Send me my meeting reports'
                        checked={editUserSnapshot.sendMeetingReports !== false}
                        onChange={this.handleToggle('sendMeetingReports')}
                    />
                    <p style={{ marginLeft: '64px', marginTop: '4px', color: '#667085', fontSize: '13px' }}>
                        We'll email you a recap when each meeting's report is ready.
                    </p>
                </div>

                <Divider />

                {/* ---- External participants ---- */}

                <div style={{ marginTop: '12px' }}>
                    <Form.Checkbox
                        toggle
                        label='External Participant Access'
                        checked={editUserSnapshot.shareWithExternalParticipants === true}
                        onChange={this.handleToggle('shareWithExternalParticipants')}
                    />
                    <p style={{ marginLeft: '64px', marginTop: '4px', color: '#667085', fontSize: '13px' }}>
                        Share a redacted version (summary + action items only — no scores or coaching) with participants from other companies.
                    </p>
                </div>

                {/* ---- Notify on share ---- */}

                <div style={{ marginTop: '16px', opacity: editUserSnapshot.shareWithExternalParticipants ? 1 : 0.55 }}>
                    <Form.Checkbox
                        toggle
                        label='Email participants when their access is granted'
                        checked={editUserSnapshot.notifyOnExternalShare !== false}
                        onChange={this.handleToggle('notifyOnExternalShare')}
                        disabled={!editUserSnapshot.shareWithExternalParticipants}
                    />
                    <p style={{ marginLeft: '64px', marginTop: '4px', color: '#667085', fontSize: '13px' }}>
                        When external sharing is on, send a notification email to participants who can view the shared report.
                    </p>
                </div>

                {error && (
                    <Message negative style={{ marginTop: '16px' }}>
                        <Message.Header>Couldn't save</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                {saved && !error && (
                    <Message positive size='tiny' style={{ marginTop: '16px' }}>
                        <Icon name='check circle' />
                        Saved.
                    </Message>
                )}

                <div style={{ marginTop: '20px' }}>
                    <Form.Button primary icon labelPosition='right' onClick={this.save}>
                        <Icon name='save' />Save Report Sharing
                    </Form.Button>
                </div>
            </Form>
        );
    }
}

export default observer(ReportSharingPanel);
